import React, { useEffect, useState, useCallback } from "react";
import { FaCalendarAlt, FaUser } from "react-icons/fa";
import { MdAccessTime } from "react-icons/md";
import SummaryApi from "../common";
import { encodeCourseId } from "../utils/encoder";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../helpers/AuthContext";
import nocourseImg from "../asset/nocourse.png";

const StudentDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { studentId, studentName, studentEmail } = useAuth();
  const [todayClasses, setTodayClasses] = useState([]);
  const [courseDetails, setCourseDetails] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());

  const navigate = useNavigate();

  const fetchCourses = useCallback(async () => {
    if (!studentId) {
      console.log("No student_id available in context, skipping course fetch.");
      setCourses([]);
      return;
    }

    try {
      const res = await fetch(SummaryApi.getCourseByStudentId.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ student_id: studentId }),
      });

      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        setCourses(data.data);
      } else {
        console.error("Invalid response structure for courses:", data);
        setCourses([]);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      setCourses([]);
    }
  }, [studentId]);

const getUpcomingThisWeekInfo = (course) => {
  const now = new Date();
  const today = now.getDay();
  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const shortDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const upcoming = [];

  for (let i = 0; i < 7; i++) {
    const dayIndex = (today + i) % 7;
    const dayKey = daysOfWeek[dayIndex];
    const slot = course[dayKey];

    if (slot && slot !== "N/A") {
      const { startTime } = extractTimeFromRange(slot);
      if (startTime) {
        const [h, m] = startTime.split(":").map(Number);
        const classTime = new Date();
        classTime.setDate(now.getDate() + i);
        classTime.setHours(h, m, 0, 0);

        if (classTime > now) {
          upcoming.push({
            day: shortDays[dayIndex],
            daysFromNow: i,
            timeSlot: slot,
          });
        }
      }
    }
  }

  if (upcoming.length === 0) {
    const start = course.start_date ? new Date(course.start_date) : null;
    if (start && start > now) {
      const daysLeft = Math.ceil((start - now) / 86400000);
      return {
        type: "not-started",
        daysLeftText: daysLeft === 0 ? "Today" : daysLeft === 1 ? "Tomorrow" : `In ${daysLeft} days`,
      };
    }
    return { type: "none" };
  }

  upcoming.sort((a, b) => a.daysFromNow - b.daysFromNow);
  const uniqueDays = [...new Set(upcoming.map(c => c.day))];
  const daysText = uniqueDays.length > 3
    ? `${uniqueDays.slice(0, 2).join(", ")} +${uniqueDays.length - 2}`
    : uniqueDays.join(", ");

  const first = upcoming[0];
  return {
    type: "scheduled",
    daysText,
    timeRange: formatTimeRange({ time_slot: first.timeSlot }),
    duration: getDuration({ time_slot: first.timeSlot }),
    daysUntil: first.daysFromNow,
    daysLeftText: first.daysFromNow === 0
      ? "Today"
      : first.daysFromNow === 1
      ? "Tomorrow"
      : `In ${first.daysFromNow} days`,
  };
};

  const fetchTodayClasses = useCallback(async () => {
    if (!studentId) {
      console.log("No student_id available in context, skipping today classes fetch.");
      setTodayClasses([]);
      return;
    }

    try {
      const res = await fetch(SummaryApi.studentdashboardtodayclass.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ student_id: studentId }),
      });

      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        const dayOfWeek = new Date().toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
        const processedData = data.data
          .map(cls => ({ ...cls, time_slot: cls[dayOfWeek] || "N/A" }))
          .filter(cls => cls.time_slot !== "N/A");
        setTodayClasses(processedData);
      } else {
        console.error("Invalid response structure for today classes:", data);
        setTodayClasses([]);
      }
    } catch (error) {
      console.error("Error fetching today classes:", error);
      setTodayClasses([]);
    }
  }, [studentId]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchCourses(), fetchTodayClasses()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchCourses, fetchTodayClasses]);

  useEffect(() => {
    const details = {};
    courses.forEach((c) => {
      details[c.course_id] = { meeting_link: c.meeting_link };
    });
    setCourseDetails(details);
  }, [courses]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const extractTimeFromRange = (timeRange) => {
    if (!timeRange || timeRange === "N/A") return { startTime: null, endTime: null };
    try {
      const [start, end] = timeRange.split("-");
      const startTime = start.slice(0, 5) + ":00";
      const endTime = end.slice(0, 5) + ":00";
      return { startTime, endTime };
    } catch (error) {
      return { startTime: null, endTime: null };
    }
  };

  const getDuration = (scheduleItem) => {
    const timeRange = scheduleItem.time_slot;

    if (!timeRange || timeRange === "N/A") return "N/A";

    const { startTime, endTime } = extractTimeFromRange(timeRange);

    if (!startTime || !endTime) return "N/A";

    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  const getCountdownText = (scheduleItem) => {
    const timeRange = scheduleItem.time_slot;

    if (!timeRange || timeRange === "N/A")
      return (
        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      );

    const { startTime, endTime } = extractTimeFromRange(timeRange);

    if (!startTime || !endTime)
      return (
        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      );

    const now = currentTime;
    const today = new Date().toISOString().split("T")[0];

    const startDateTime = new Date(`${today}T${startTime}`);
    const endDateTime = new Date(`${today}T${endTime}`);

    const diffMs = startDateTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours > 0) {
      const hours = Math.floor(diffHours);
      const minutes = Math.floor((diffHours - hours) * 60);
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      let countdownText = "Starts in ";
      if (hours > 0) {
        countdownText += `${hours} hr${hours !== 1 ? "s" : ""} `;
      }
      if (minutes > 0 || hours > 0) {
        countdownText += `${minutes} min${minutes !== 1 ? "s" : ""} `;
      }
      countdownText += `${seconds} sec${seconds !== 1 ? "s" : ""}`;
      return (
        <div className="flex items-center text-green-600 dark:text-green-400">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2a10 10 0 0110 10c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 4v6l3 3"></path>
          </svg>
          {countdownText.trim()}
        </div>
      );
    } else if (now >= endDateTime) {
      return (
        <div className="flex items-center text-green-600 dark:text-green-400">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Completed
        </div>
      );
    } else if (now >= startDateTime && now < endDateTime) {
      const remainingMs = endDateTime - now;
      const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
      const remainingMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      const remainingSeconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

      let countdownText = "Ongoing - ends in ";
      if (remainingHours > 0) {
        countdownText += `${remainingHours} hr${remainingHours !== 1 ? "s" : ""} `;
      }
      if (remainingMinutes > 0 || remainingHours > 0) {
        countdownText += `${remainingMinutes} min${remainingMinutes !== 1 ? "s" : ""} `;
      }
      countdownText += `${remainingSeconds} sec${remainingSeconds !== 1 ? "s" : ""}`;
      return (
        <div className="flex items-center text-green-600 dark:text-green-400">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2a10 10 0 0110 10c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 4v6l3 3"></path>
          </svg>
          {countdownText.trim()}
        </div>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      );
    }
  };

  const isClassOngoing = (scheduleItem) => {
    const timeRange = scheduleItem.time_slot;

    if (!timeRange || timeRange === "N/A") return false;

    const { startTime, endTime } = extractTimeFromRange(timeRange);
    if (!startTime || !endTime) return false;

    const now = currentTime;
    const today = new Date().toISOString().split("T")[0];
    const startDateTime = new Date(`${today}T${startTime}`);
    const endDateTime = new Date(`${today}T${endTime}`);

    return now >= startDateTime && now < endDateTime;
  };

  const formatTimeRange = (scheduleItem) => {
    const timeRange = scheduleItem.time_slot;

    if (!timeRange || timeRange === "N/A") return "N/A";

    const { startTime, endTime } = extractTimeFromRange(timeRange);
    if (!startTime || !endTime) return "N/A";

    const formatTo12Hour = (time) => {
      const [hours, minutes] = time.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const adjustedHours = hours % 12 || 12;
      return `${adjustedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    return `${formatTo12Hour(startTime)} - ${formatTo12Hour(endTime)}`;
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return "N/A";
    try {
      return dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const joinClass = (item) => {
    const course = courseDetails[item.course_id];
    if (!course) return;
    const baseMeetingUrl = course.meeting_link || "https://meet.google.com/abc-defg-hij";
    const meetingUrlWithEmail = baseMeetingUrl.includes("zoom.us")
      ? `${baseMeetingUrl}?uname=${encodeURIComponent(studentEmail)}`
      : baseMeetingUrl;
    window.open(meetingUrlWithEmail, "_blank", "noopener,noreferrer");
  };

  const calculateProgress = (course) => {
    if (!course.start_date || !course.end_date) return 0;
    const start = new Date(course.start_date);
    const end = new Date(course.end_date);
    const now = new Date();
    if (now < start) return 0;
    if (now > end) return 100;
    const total = end - start;
    const elapsed = now - start;
    return Math.round((elapsed / total) * 100);
  };

  // NEW: Calculate "X days left" for upcoming courses
  const getDaysUntilStart = (startDateStr) => {
    if (!startDateStr) return "N/A";
    const start = new Date(startDateStr);
    const now = new Date();
    const diffMs = start - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Starts today";
    if (diffDays === 1) return "Starts tomorrow";
    if (diffDays > 1) return `${diffDays}d left`;
    return "Started";
  };

  // Update the upcomingCourses filter to include courses that haven't started
// Replace your current upcomingCourses filter with this:
const upcomingCourses = courses.filter(course => {
  const now = new Date();
  const startDate = course.start_date ? new Date(course.start_date) : null;
  const info = getUpcomingThisWeekInfo(course);

  return (startDate && startDate > now) || info.type === "scheduled";
});

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Continue Learning */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-[#1f4774] dark:text-blue-400">
            Welcome, {studentName || "Student"}! Continue Learning
          </h2>
          {loading ? (
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">Loading courses...</p>
          ) : courses.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-4 sm:p-6">
              <img
                src={nocourseImg}
                alt="No Courses"
                className="w-48 h-48 sm:w-64 sm:h-64 object-contain mb-4 sm:mb-6"
              />
              <button
                onClick={() => navigate("/")}
                className="bg-[#1f4774] dark:bg-blue-700 text-white px-4 py-1 sm:px-6 sm:py-3 rounded-lg shadow hover:bg-[#16375c] dark:hover:bg-blue-600 transition-all text-sm sm:text-base"
              >
                Choose Course
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {courses.map((course, index) => (
                <div
                  key={index}
                  className="rounded-xl shadow-md bg-white dark:bg-gray-800 overflow-hidden flex flex-col h-full min-h-[320px] sm:min-h-[340px]"
                >
                  <img
                    src={
                      course?.course_image1
                        ? `${SummaryApi.Images?.url?.replace("api", "")}${course.course_image1}`
                        : "https://via.placeholder.com/150"
                    }
                    alt="course thumbnail"
                    className="w-full h-32 sm:h-40 object-cover flex-shrink-0"
                  />
                  <div className="p-3 sm:p-4 flex flex-col flex-grow">
                    <p className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 px-2 py-1 inline-block rounded mb-2">
                      {course.category_name}
                    </p>
                    <h3 className="font-bold text-base sm:text-lg mb-2 text-gray-800 dark:text-white line-clamp-2">
                      {course.course_title}
                    </h3>
                    <p className="text-yellow-500 dark:text-yellow-400 font-semibold text-sm">
                      ⭐ {course.rating}
                    </p>
                    <div className="flex items-center text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-2">
                      <FaCalendarAlt className="mr-2 flex-shrink-0" />
                      <span className="truncate">{course.course_duration}</span>
                    </div>
                    <div className="mt-auto pt-3">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full"
                          style={{ width: `${calculateProgress(course)}%` }}
                        ></div>
                      </div>
                      <p className="text-right text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">{calculateProgress(course)}%</p>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 pt-0">
                    <button
                      onClick={() =>
                        navigate(`/student-panel/course-curriculum/${btoa(course.course_id.toString())}?time_slot_id=${btoa(course.time_slot_id.toString())}`)
                      }
                      className="w-full text-white px-3 py-1 sm:px-4 sm:py-3 rounded bg-blue-900 dark:bg-blue-700 hover:bg-blue-800 dark:hover:bg-blue-600 transition-all text-sm sm:text-base"
                    >
                      View Curriculum
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Today's Classes */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-[#1f4774] dark:text-blue-400">
            Today's Classes
          </h2>
          <div className="flex flex-col gap-4 sm:gap-6">
            {loading ? (
              <p className="text-center text-gray-600 dark:text-gray-300 animate-pulse text-sm sm:text-base">Loading today's classes...</p>
            ) : todayClasses.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 sm:py-16 px-4 sm:px-6 bg-gray-50 dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-600">
                <svg
                  width="80" height="80"
                  className="sm:w-24 sm:h-24 mb-4 sm:mb-6 text-gray-400 dark:text-gray-500"
                  viewBox="0 0 120 120"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_1_2)">
                    <rect x="20" y="30" width="80" height="70" rx="8" fill="#E5E7EB" stroke="#D1D5DB" strokeWidth="2"/>
                    <rect x="30" y="40" width="60" height="8" rx="2" fill="#F3F4F6"/>
                    <rect x="30" y="52" width="60" height="38" rx="2" fill="#F9FAFB"/>
                    <line x1="40" y1="60" x2="90" y2="60" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <line x1="40" y1="70" x2="90" y2="70" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <line x1="40" y1="80" x2="90" y2="80" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <line x1="55" y1="52" x2="55" y2="90" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <line x1="70" y1="52" x2="70" y2="90" stroke="#E5E7EB" strokeWidth="1.5"/>
                    <line x1="45" y1="65" x2="65" y2="85" stroke="#EF4444" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="65" y1="65" x2="45" y2="85" stroke="#EF4444" strokeWidth="3" strokeLinecap="round"/>
                    <circle cx="35" cy="25" r="3" fill="#D1D5DB"/>
                    <circle cx="85" cy="25" r="3" fill="#D1D5DB"/>
                  </g>
                </svg>
                <p className="text-base sm:text-lg font-medium text-gray-700 dark:text-gray-200 text-center">No classes scheduled for today.</p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 text-center">Check back tomorrow or explore your enrolled courses.</p>
              </div>
            ) : (
              todayClasses.map((cls, index) => (
                <div
                  key={index}
                  className="relative rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 p-4 sm:p-6 min-h-[180px]"
                >
       <div className="pr-28 sm:pr-32 lg:pr-36">
      <h3 className="font-bold text-sm sm:text-lg lg:text-xl text-[#1f4774] dark:text-blue-400 line-clamp-2 leading-tight">
  {cls.course_title}
</h3>
       <div className="flex flex-col sm:flex-row sm:items-center text-gray-600 dark:text-gray-400 text-sm sm:text-base lg:text-lg mt-3 sm:mt-4 gap-2 sm:gap-0">
                    <div className="flex items-center">
                      <FaCalendarAlt className="mr-2.5 text-blue-600 dark:text-blue-400 flex-shrink-0 w-5 h-5" />
                      <span className="font-medium">{formatDate(new Date())}</span>
                    </div>
                    <div className="flex items-center sm:ml-6">
                      <MdAccessTime className="mr-2.5 text-blue-600 dark:text-blue-400 flex-shrink-0 w-5 h-5" />
                      <span className="font-semibold">
                        {formatTimeRange(cls)} <span className="text-gray-500">({getDuration(cls)})</span>
                      </span>
                    </div>
                  </div>
  <div className="flex items-center text-gray-700 dark:text-gray-300 text-sm sm:text-base lg:text-md mt-4 font-medium">
  <FaUser className="mr-3 text-blue-600 dark:text-blue-400 flex-shrink-0 w-6 h-6" />
  <span className="truncate">{cls.instructor_name}</span>
</div>
                    <div className="mt-3 sm:mt-4 flex justify-start">

                  {/* Countdown / Status */}
                <div className="mt-3 sm:mt-4 flex justify-start">
    <div className="text-xs sm:text-sm break-words max-w-[200px] sm:max-w-none">  {/* ← updated */}
      {getCountdownText(cls)}
    </div>
  </div>
                    </div>
                  </div>
                  <div className="absolute bottom-4 right-4">
                <div className="absolute bottom-5 right-5">
                  <button
                    onClick={() => joinClass(cls)}
                    disabled={!isClassOngoing(cls)}
                    className={`bg-[#1f4774] dark:bg-blue-700 text-white w-32 sm:w-36 h-11 sm:h-12 flex items-center justify-center rounded-tr-xl rounded-bl-xl transition-all text-sm sm:text-base font-semibold shadow-md ${
                      isClassOngoing(cls)
                        ? 'opacity-100 hover:bg-[#16375c] dark:hover:bg-blue-600'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    Join Class
                  </button>
                </div>
                  </div>
                </div>
              ))
            )}
          </div>

{/* Upcoming Classes Section */}
<h2 className="text-xl sm:text-2xl font-bold mt-10 mb-6 text-[#1f4774] dark:text-blue-400">
  Upcoming Classes
</h2>

<div className="flex flex-col gap-5">
  {loading ? (
    <div className="text-center py-12 text-gray-500 dark:text-gray-400 animate-pulse">
      Loading upcoming classes...
    </div>
  ) : upcomingCourses.length === 0 ? (
    <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700">
      <svg className="w-20 h-20 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <p className="text-lg font-medium text-gray-700 dark:text-gray-200">No upcoming classes</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Enjoy your break or explore new courses!</p>
    </div>
  ) : (
    upcomingCourses.map((course) => {
      const info = getUpcomingThisWeekInfo(course);
      const startDate = course.start_date ? new Date(course.start_date) : null;
      const now = new Date();
      const hasNotStarted = startDate && startDate > now;

      return (
        <div
          key={course.course_id}
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-xl transition-all duration-300 group"
        >
          <div className="flex flex-col h-full">
            {/* Course Title */}
            <h3 className="text-lg sm:text-xl font-bold text-[#1f4774] dark:text-blue-400 mb-4 line-clamp-2">
              {course.course_title}
            </h3>

            <div className="space-y-4 flex-1">
              {/* Course hasn't started */}
              {hasNotStarted && (
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <FaCalendarAlt className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Course starts on</span>
                    <span className="ml-2 font-bold text-blue-600 dark:text-blue-300">
                      {startDate.toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              )}

              {/* Upcoming classes this week */}
              {info.type === "scheduled" && (
                <>
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <FaCalendarAlt className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div>
                      <span className="font-medium">Classes on:</span>
                      <span className="ml-2 font-bold text-blue-600 dark:text-blue-300">
                        {info.daysText}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <MdAccessTime className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <span className="font-semibold">
                      {info.timeRange}
                      {info.duration && (
                        <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal">
                          ({info.duration})
                        </span>
                      )}
                    </span>
                  </div>
                </>
              )}

    
            </div>
<div className="flex items-center text-gray-700 dark:text-gray-300">
                <span className="font-medium">
         {/* INSTRUCTOR NAME — FIXED: now takes from todayClasses if not in course */}
              {(() => {
                const cls = todayClasses.find(c => c.course_id === course.course_id);
                return (
                  <div className="flex items-center text-gray-700 dark:text-gray-300">
                    <FaUser className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <span className="font-medium">
                      {cls?.instructor_name || course.instructor_name || "Instructor"}
                    </span>
                  </div>
                );
              })()}
                </span>
              </div>
            {/* Bottom: Status + Batch */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">

              <div className="text-xs sm:text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 font-medium">
                {course.batch_name}
              </div>
            </div>
          </div>
        </div>
      );
    })
  )}
</div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;